import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  UserGroupIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  ChevronLeftIcon,
} from '@heroicons/react/24/outline';
import { guardianApi } from '../../services/api/guardian';
import { Card, H1, H2, H3, Text, Badge, Spinner, Alert, Tabs } from '../../components/ui';
import { formatDate, formatDateLong } from '../../utils/date';

export const GuardianChildDetail: React.FC = () => {
  const { childId } = useParams<{ childId: string }>();
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch child details
  const { data: child, isLoading: childLoading, error: childError } = useQuery({
    queryKey: ['guardian-child', childId],
    queryFn: () => guardianApi.portal.getChild(childId!),
    enabled: !!childId,
  });

  // Fetch observations
  const { data: observations, isLoading: obsLoading } = useQuery({
    queryKey: ['guardian-child-observations', childId],
    queryFn: () => guardianApi.portal.getChildObservations(childId!, { limit: 100 }),
    enabled: !!childId,
  });

  // Fetch assessments
  const { data: assessments, isLoading: assessLoading } = useQuery({
    queryKey: ['guardian-child-assessments', childId],
    queryFn: () => guardianApi.portal.getChildAssessments(childId!, { limit: 100 }),
    enabled: !!childId,
  });

  if (childLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (childError || !child) {
    return (
      <Alert variant="error">
        Failed to load child details. Please try again later.
      </Alert>
    );
  }

  const tabItems = [
    { id: 'overview', label: 'Overview', icon: UserGroupIcon },
    { id: 'observations', label: `Observations (${observations?.length || 0})`, icon: ClipboardDocumentListIcon },
    { id: 'assessments', label: `Assessments (${assessments?.length || 0})`, icon: DocumentTextIcon },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link to="/guardian/children" className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-4">
          <ChevronLeftIcon className="w-4 h-4 mr-1" />
          Back to Children
        </Link>

        <div className="flex items-start justify-between">
          <div>
            <H1>{child.first_name} {child.last_name}</H1>
            <Text className="mt-2 text-gray-600 dark:text-gray-400">
              {child.grade?.name} - {child.classroom?.name}
            </Text>
          </div>
          <div className="flex-shrink-0">
            <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
              <UserGroupIcon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        items={tabItems}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Basic Information */}
          <Card className="p-6">
            <H2 className="mb-4">Basic Information</H2>
            <div className="space-y-3">
              <div>
                <Text className="text-sm text-gray-500 dark:text-gray-400">Full Name</Text>
                <Text className="font-medium">{child.first_name} {child.last_name}</Text>
              </div>
              <div>
                <Text className="text-sm text-gray-500 dark:text-gray-400">Date of Birth</Text>
                <Text className="font-medium">
                  {formatDateLong(child.date_of_birth)}
                </Text>
              </div>
              <div>
                <Text className="text-sm text-gray-500 dark:text-gray-400">Age</Text>
                <Text className="font-medium">
                  {(child.age != null
                    ? child.age
                    : child.date_of_birth
                      ? Math.floor((Date.now() - new Date(child.date_of_birth).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
                      : null
                  ) ?? 'Unknown'} years old
                </Text>
              </div>
              <div>
                <Text className="text-sm text-gray-500 dark:text-gray-400">Gender</Text>
                <Text className="font-medium">
                  {child.gender === 'M' ? 'Male' : child.gender === 'F' ? 'Female' : 'Other'}
                </Text>
              </div>
            </div>
          </Card>

          {/* Academic Information */}
          <Card className="p-6">
            <H2 className="mb-4">Academic Information</H2>
            <div className="space-y-3">
              <div>
                <Text className="text-sm text-gray-500 dark:text-gray-400">Grade</Text>
                <Text className="font-medium">{child.grade?.name || 'Not assigned'}</Text>
              </div>
              <div>
                <Text className="text-sm text-gray-500 dark:text-gray-400">Classroom</Text>
                <Text className="font-medium">{child.classroom?.name || 'Not assigned'}</Text>
              </div>
              {child.relationship_type && (
                <div>
                  <Text className="text-sm text-gray-500 dark:text-gray-400">Relationship</Text>
                  <Badge variant="primary">
                    {child.relationship_type}
                  </Badge>
                </div>
              )}
            </div>
          </Card>

          {/* Statistics */}
          <Card className="p-6 md:col-span-2">
            <H2 className="mb-4">Activity Summary</H2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                  {observations?.length || 0}
                </div>
                <Text className="text-sm text-gray-500 dark:text-gray-400">Total Observations</Text>
              </div>
              <div className="text-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {assessments?.length || 0}
                </div>
                <Text className="text-sm text-gray-500 dark:text-gray-400">Total Assessments</Text>
              </div>
              <div className="text-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {child.is_active !== false ? 'Active' : 'Inactive'}
                </div>
                <Text className="text-sm text-gray-500 dark:text-gray-400">Status</Text>
              </div>
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'observations' && (
        <div className="space-y-4">
          {obsLoading ? (
            <div className="flex items-center justify-center h-64">
              <Spinner size="lg" />
            </div>
          ) : observations && observations.length > 0 ? (
            observations.map((observation) => (
              <Card key={observation.id} className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <Badge variant="secondary" size="sm">
                      {observation.category}
                    </Badge>
                    {observation.is_milestone && (
                      <Badge variant="success" size="sm" className="ml-2">
                        Milestone
                      </Badge>
                    )}
                    {observation.is_concern && (
                      <Badge variant="warning" size="sm" className="ml-2">
                        Concern
                      </Badge>
                    )}
                  </div>
                  <Text className="text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(observation.observation_date)}
                  </Text>
                </div>

                <Text className="text-gray-700 dark:text-gray-300 mb-3">
                  {observation.narrative}
                </Text>

                <div className="flex items-center justify-between text-sm">
                  <Text className="text-gray-500 dark:text-gray-400">
                    By: {observation.teacher_name || `${observation.teacher?.first_name || ''} ${observation.teacher?.last_name || ''}`.trim() || 'Unknown'}
                  </Text>
                  {observation.learning_area && (
                    <Badge variant="info" size="sm">
                      {typeof observation.learning_area === 'string'
                        ? observation.learning_area
                        : observation.learning_area}
                    </Badge>
                  )}
                </div>
              </Card>
            ))
          ) : (
            <Card className="p-8">
              <div className="text-center">
                <ClipboardDocumentListIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                <H3 className="mt-4 text-gray-900 dark:text-gray-100">No Observations Yet</H3>
                <Text className="mt-2 text-gray-500 dark:text-gray-400">
                  No published observations available for {child.first_name}.
                </Text>
              </div>
            </Card>
          )}
        </div>
      )}

      {activeTab === 'assessments' && (
        <div className="space-y-4">
          {assessLoading ? (
            <div className="flex items-center justify-center h-64">
              <Spinner size="lg" />
            </div>
          ) : assessments && assessments.length > 0 ? (
            assessments.map((assessment) => (
              <Card key={assessment.id} className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <H3 className="text-base">{assessment.learning_area_name || assessment.learning_area?.name || 'Learning Area'}</H3>
                    <Text className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {assessment.learning_outcome?.description || ''}
                    </Text>
                  </div>
                  <Text className="text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(assessment.assessment_date)}
                  </Text>
                </div>

                {assessment.score !== null && assessment.score !== undefined && (
                  <div className="mt-3">
                    <div className="flex items-center space-x-2">
                      <Badge variant="success">
                        Score: {assessment.score}/{assessment.assessment_scale?.max_value}
                      </Badge>
                      <Text className="text-sm text-gray-500 dark:text-gray-400">
                        ({assessment.assessment_scale?.name})
                      </Text>
                    </div>
                  </div>
                )}

                {assessment.notes && (
                  <Text className="mt-3 text-sm text-gray-700 dark:text-gray-300">
                    {assessment.notes}
                  </Text>
                )}
              </Card>
            ))
          ) : (
            <Card className="p-8">
              <div className="text-center">
                <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                <H3 className="mt-4 text-gray-900 dark:text-gray-100">No Assessments Yet</H3>
                <Text className="mt-2 text-gray-500 dark:text-gray-400">
                  No published assessments available for {child.first_name}.
                </Text>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};